import { useApolloClient } from "@apollo/client";
import { AgentDialogFormValues } from "../../features/agent/agentDialogFormValues.js";
import { assertNever } from "@msys/common";
import { RadioGroup } from "@msys/ui";
import { Box, DialogContentText, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { AddressInput, Salutation } from "../../../clients/graphqlTypes.js";
import { EmailField } from "../../commons/form-fields/EmailField.js";
import { PhoneField } from "../../commons/form-fields/PhoneField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { FormikClearErrorsOnChange } from "../../commons/FormikClearErrorsOnChange.js";
import {
  FormikObserveState,
  FormikStateObserver,
} from "../../commons/FormikStateObserver.js";
import { AddressField } from "../addresses/AddressField.js";
import {
  CreateCrmCompany_CrmCompanyFragment,
  useCreateCrmCompanyAsIndividualMutation,
  useCreateCrmCompanyMutation,
} from "../crm-companies/CrmCompany.generated.js";
import { useCreateCrmPersonMutation } from "../crm-persons/CrmPerson.generated.js";
import { useEmailAddressValidationSchema } from "../email/validateEmailAddress.js";
import { UserTitleWithNameField } from "../users/UserTitleWithNameField.js";
import {
  ALL_CONTACT_TYPES,
  CrmContactType,
  useCrmContactTypes,
} from "./useCrmContactTypes.js";

const DEFAULT_CONTACT_TYPE: CrmContactType = "INDIVIDUAL";

export interface CrmCreateFormPrefill {
  email?: string;
  companyName?: string;
  firstname?: string;
  familyname?: string;
  contactType?: CrmContactType;
  // Only meaningful for an INDIVIDUAL prefill: the sharer's own stored
  // salutation, so the form doesn't invent one. Falls back to the form's
  // usual default when absent.
  salutation?: Salutation;
}

interface Props {
  allowedContactTypes?: CrmContactType[];
  handleComplete: (
    organisation: CreateCrmCompany_CrmCompanyFragment,
    userId?: string
  ) => Promise<void> | void;
  refetchQueries?: string[];
  prefill?: CrmCreateFormPrefill;
  formId: string;
  handleFormikObserveStateChange: (state: FormikObserveState) => void;
}

export const CrmCreateForm = ({
  allowedContactTypes = ALL_CONTACT_TYPES,
  handleComplete,
  refetchQueries,
  prefill,
  formId,
  handleFormikObserveStateChange,
}: Props) => {
  const { contactTypeOptions } = useCrmContactTypes();

  const [crmContactType, setCrmContactType] = React.useState<CrmContactType>(
    prefill?.contactType && allowedContactTypes.includes(prefill.contactType)
      ? prefill.contactType
      : allowedContactTypes.includes(DEFAULT_CONTACT_TYPE)
        ? DEFAULT_CONTACT_TYPE
        : allowedContactTypes[0]
  );

  return (
    <Stack spacing={2}>
      <RadioGroup<CrmContactType>
        inline
        options={contactTypeOptions.filter(({ value }) =>
          allowedContactTypes.includes(value)
        )}
        value={crmContactType}
        onChange={value => setCrmContactType(value)}
      />
      {crmContactType === "COMPANY" ? (
        <AddCrmCompanyForm
          formId={formId}
          handleComplete={handleComplete}
          refetchQueries={refetchQueries}
          handleFormikObserveStateChange={handleFormikObserveStateChange}
          prefill={prefill}
        />
      ) : crmContactType === "INDIVIDUAL" ? (
        <AddCrmIndividualForm
          formId={formId}
          handleComplete={handleComplete}
          refetchQueries={refetchQueries}
          handleFormikObserveStateChange={handleFormikObserveStateChange}
          prefill={prefill}
        />
      ) : (
        assertNever(crmContactType)
      )}
    </Stack>
  );
};

// ADD COMPANY

interface AddCompanyFormValues {
  companyName: string;
  companyAddress: AddressInput | null;
  contactPersonTitle: Salutation;
  contactPersonFirstname: string;
  contactPersonFamilyname: string;
  contactPersonPhone: string;
  contactPersonEmail: string;
  personHasDifferentAddress: boolean;
  contactPersonAddress: AddressInput | null;
}

function AddCrmCompanyForm({
  formId,
  refetchQueries,
  handleComplete,
  handleFormikObserveStateChange,
  prefill,
}: {
  formId: string;
  refetchQueries?: string[];
  handleComplete: (
    organisation: CreateCrmCompany_CrmCompanyFragment,
    userId?: string
  ) => void;
  handleFormikObserveStateChange: (state: FormikObserveState) => void;
  prefill?: CrmCreateFormPrefill;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslate([
    "UserDetails",
    "OrganisationProfile",
    "CrmOrganisations",
    "Global",
  ]);

  const client = useApolloClient();

  const { createEmailAddressValidationSchema } =
    useEmailAddressValidationSchema();

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape(
        {
          companyName: Yup.string()
            .label(
              t("Company Name", {
                ns: "UserDetails",
              })
            )
            .required()
            .min(1),
          companyAddress: Yup.object()
            .shape({
              streetLine1: Yup.string(),
              postalCode: Yup.string(),
              city: Yup.string(),
            })
            .label(t("Address", { ns: "CrmOrganisations" }))
            .nullable(),
          contactPersonTitle: Yup.string()
            .label(t("Salutation", { ns: "Global" }))
            .required(),
          contactPersonFirstname: Yup.string()
            .label(t("First name", { ns: "UserDetails" }))
            .when("contactPersonFamilyname", {
              is: (contactPersonFamilyname: string) =>
                !!contactPersonFamilyname,
              then: schema => schema.required().min(1),
            }),
          contactPersonFamilyname: Yup.string()
            .label(t("Last name", { ns: "UserDetails" }))
            .when("contactPersonFirstname", {
              is: (contactPersonFirstname: string) => !!contactPersonFirstname,
              then: schema => schema.required().min(1),
            }),
          contactPersonPhone: Yup.string().label(
            t("Phone", { ns: "UserDetails" })
          ),
          contactPersonEmail: createEmailAddressValidationSchema(
            t("Email", { ns: "UserDetails" })
          ),
          contactPersonAddress: Yup.object()
            .shape({
              streetLine1: Yup.string(),
              postalCode: Yup.string(),
              city: Yup.string(),
            })
            .label(t("Address", { ns: "CrmOrganisations" }))
            .nullable(),
        },
        [
          ["contactPersonTitle", "contactPersonFirstname"],
          ["contactPersonTitle", "contactPersonFamilyname"],
          ["contactPersonFirstname", "contactPersonFamilyname"],
        ]
      ),
    [createEmailAddressValidationSchema, t]
  );

  const initialValues = React.useMemo(
    (): AddCompanyFormValues => ({
      companyName: prefill?.companyName ?? "",
      companyAddress: null,
      contactPersonTitle: "mr",
      contactPersonFirstname: prefill?.firstname ?? "",
      contactPersonFamilyname: prefill?.familyname ?? "",
      contactPersonPhone: "",
      contactPersonEmail: prefill?.email ?? "",
      personHasDifferentAddress: false,
      contactPersonAddress: null,
    }),
    [
      prefill?.companyName,
      prefill?.email,
      prefill?.familyname,
      prefill?.firstname,
    ]
  );

  const [createCrmCompany] = useCreateCrmCompanyMutation({
    client,
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const [createCrmPerson] = useCreateCrmPersonMutation({
    client,
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const onSubmit = async ({
    companyName,
    companyAddress,
    contactPersonTitle,
    contactPersonFirstname,
    contactPersonFamilyname,
    contactPersonPhone,
    contactPersonEmail,
    personHasDifferentAddress,
    contactPersonAddress,
  }: AddCompanyFormValues) => {
    const { data } = await createCrmCompany({
      variables: {
        input: {
          name: companyName,
          phones: contactPersonPhone
            ? [{ main: true, type: "WORK", number: contactPersonPhone }]
            : undefined, // FIXME
          email: contactPersonEmail, // FIXME
          billingAddress: companyAddress,
        },
      },
    });

    if (!data) throw new Error("Crm company was not created");

    const organisation = data.createCrmCompany.crmCompany;
    let userId: string | undefined = undefined;

    if (contactPersonFirstname || contactPersonFamilyname) {
      const { data } = await createCrmPerson({
        variables: {
          input: {
            crmCompanyId: organisation.id,
            firstname: contactPersonFirstname,
            familyname: contactPersonFamilyname,
            title: contactPersonTitle,
            address: contactPersonAddress,
            phones: contactPersonPhone
              ? [{ main: true, type: "WORK", number: contactPersonPhone }]
              : undefined,
            email: contactPersonEmail,
          },
        },
      });

      userId = data?.createCrmPerson.crmPerson.id;
    }

    handleComplete(organisation, userId);
    enqueueSnackbar(
      t("Client created", {
        ns: "UserDetails",
      })
    );
  };

  return (
    <Formik<AddCompanyFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {formikProps => (
        <Form id={formId}>
          <AgentDialogFormValues />
          <Stack spacing={1}>
            <TextField
              name="companyName"
              label={t("Company Name", {
                ns: "UserDetails",
              })}
              required
            />
            <AddressField
              label={t("Address", {
                ns: "CrmOrganisations",
              })}
              name="companyAddress"
            />

            <DialogContentText>
              {t("Contact person", {
                ns: "OrganisationProfile",
              })}
            </DialogContentText>
            <Box>
              <UserTitleWithNameField
                titleField="contactPersonTitle"
                firstnameField="contactPersonFirstname"
                familynameField="contactPersonFamilyname"
                isRequiredOption="BOTH_OR_NONE"
              />
            </Box>
            <PhoneField
              label={t("Phone", { ns: "UserDetails" })}
              name="contactPersonPhone"
            />
            <EmailField
              label={t("Email", {
                ns: "UserDetails",
              })}
              name="contactPersonEmail"
            />
            {/*<CheckboxField
              name={"personHasDifferentAddress"}
              label={t("Different address", { ns: "UserDetails" })}
            />
            {formikProps.values.personHasDifferentAddress && (
              <AddressField
                label={t("Address", {
                  ns: "CrmOrganisations",
                })}
                name="contactPersonAddress"
              />
            )}*/}
          </Stack>
          <FormikStateObserver callback={handleFormikObserveStateChange} />
          <FormikClearErrorsOnChange />
        </Form>
      )}
    </Formik>
  );
}

// ADD INDIVIDUAL

interface AddIndividualFormValues {
  contactPersonTitle: Salutation;
  contactPersonFirstname: string;
  contactPersonFamilyname: string;
  phone: string;
  email: string;
  address: AddressInput | null;
}

function AddCrmIndividualForm({
  formId,
  refetchQueries,
  handleComplete,
  handleFormikObserveStateChange,
  prefill,
}: {
  formId: string;
  refetchQueries?: string[];
  handleComplete: (
    organisation: CreateCrmCompany_CrmCompanyFragment,
    userId: string
  ) => void;
  handleFormikObserveStateChange: (state: FormikObserveState) => void;
  prefill?: CrmCreateFormPrefill;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslate(["UserDetails", "Global", "CrmOrganisations"]);

  const client = useApolloClient();

  const { createEmailAddressValidationSchema } =
    useEmailAddressValidationSchema();

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        contactPersonTitle: Yup.string()
          .label(
            t("Salutation", {
              ns: "Global",
            })
          )
          .required(),
        contactPersonFirstname: Yup.string()
          .label(
            t("First name", {
              ns: "UserDetails",
            })
          )
          .required()
          .min(1),
        contactPersonFamilyname: Yup.string()
          .label(
            t("Last name", {
              ns: "UserDetails",
            })
          )
          .required()
          .min(1),
        phone: Yup.string().label(
          t("Phone", {
            ns: "UserDetails",
          })
        ),
        email: createEmailAddressValidationSchema(
          t("Email", {
            ns: "UserDetails",
          })
        ),
        address: Yup.object()
          .shape({
            streetLine1: Yup.string(),
            postalCode: Yup.string(),
            city: Yup.string(),
          })
          .label(t("Address", { ns: "CrmOrganisations" }))
          .nullable(),
      }),
    [createEmailAddressValidationSchema, t]
  );

  const initialValues = React.useMemo(
    (): AddIndividualFormValues => ({
      contactPersonTitle: prefill?.salutation ?? "mr",
      contactPersonFirstname: prefill?.firstname ?? "",
      contactPersonFamilyname: prefill?.familyname ?? "",
      phone: "",
      email: prefill?.email ?? "",
      address: null,
    }),
    [
      prefill?.email,
      prefill?.familyname,
      prefill?.firstname,
      prefill?.salutation,
    ]
  );

  const [createCrmCompany] = useCreateCrmCompanyAsIndividualMutation({
    client,
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const onSubmit = async ({
    contactPersonTitle,
    contactPersonFirstname,
    contactPersonFamilyname,
    phone,
    email,
    address,
  }: AddIndividualFormValues) => {
    const { data } = await createCrmCompany({
      variables: {
        input: {
          phones: phone
            ? [{ main: true, type: "WORK", number: phone }]
            : undefined,
          email: email,
          billingAddress: address,
          title: contactPersonTitle,
          firstname: contactPersonFirstname,
          familyname: contactPersonFamilyname,
        },
      },
    });

    if (!data) throw new Error("Crm company was not created");

    const organisation = data.createCrmCompanyAsIndividual.crmCompany;

    const userId = organisation.contactPerson?.id;

    if (!userId) throw new Error("CRM user was not created");

    handleComplete(organisation, userId);
    enqueueSnackbar(
      t("Client created", {
        ns: "UserDetails",
      })
    );
  };

  return (
    <Formik<AddIndividualFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
    >
      <Form id={formId}>
        <AgentDialogFormValues />
        <Stack spacing={1}>
          <Box>
            <UserTitleWithNameField
              titleField="contactPersonTitle"
              firstnameField="contactPersonFirstname"
              isRequiredOption="BOTH"
              familynameField="contactPersonFamilyname"
            />
          </Box>
          <PhoneField label={t("Phone", { ns: "UserDetails" })} name="phone" />
          <EmailField label={t("Email", { ns: "UserDetails" })} name="email" />
          <AddressField
            label={t("Address", { ns: "CrmOrganisations" })}
            name="address"
          />
        </Stack>
        <FormikStateObserver callback={handleFormikObserveStateChange} />
        <FormikClearErrorsOnChange />
      </Form>
    </Formik>
  );
}
